import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { toast } from "@/components/ui/sonner";
import { abandonStripeCheckoutSession } from "@/hera-api/bulk-payment-api";
import { getLicenseFromCheckoutSessionOptions } from "@/hera-api/client/@tanstack/react-query.gen";
import { useAuthQuerySetup } from "@/hooks/use-auth-query-setup";
import { useRegistrantOrgPermissions } from "@/hooks/use-registrant-org-permissions";
import { invalidateRegistrantDashboardLists } from "@/lib/registrant-dashboard-query-invalidation";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Link, createFileRoute, useNavigate } from "@tanstack/react-router";
import { AlertCircle, CreditCard, Home } from "lucide-react";
import * as React from "react";

export const Route = createFileRoute("/registrant/payment-cancel")({
  component: PaymentCancelScreen,
});

export function PaymentCancelScreen() {
  const { isAuthSetupComplete } = useAuthQuerySetup();
  const { canMakePayments } = useRegistrantOrgPermissions();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const searchParams = new URLSearchParams(window.location.search);
  const sessionId = searchParams.get("sessionId");
  const isBulkPaymentFlow = searchParams.get("flow") === "bulk";

  React.useEffect(() => {
    if (!sessionId) {
      void invalidateRegistrantDashboardLists(queryClient);
      return;
    }
    void (async () => {
      try {
        await abandonStripeCheckoutSession(sessionId);
      } catch {
        // Best-effort: still refresh dashboard if abandon fails (e.g. already finalised).
      }
      await invalidateRegistrantDashboardLists(queryClient);
    })();
  }, [queryClient, sessionId]);

  // Get license ID from checkout session
  const { data: licenseData, isLoading: isLicenseLoading } = useQuery({
    ...getLicenseFromCheckoutSessionOptions({
      path: {
        checkout_session_id: sessionId || "",
      },
    }),
    enabled: !!sessionId,
  });

  const handlePayNow = () => {
    if (isBulkPaymentFlow || licenseData?.is_bulk) {
      navigate({ to: "/registrant/bulk-payment" });
      return;
    }

    if (!licenseData?.license_id) {
      toast({
        title: "Error",
        description: "Unable to get license information",
        variant: "destructive",
      });
      return;
    }

    navigate({
      to: "/registrant/licenses/$licenseId/payment",
      params: { licenseId: licenseData.license_id },
      search: { paymentMethod: "card" },
    });
  };

  return (
    <div className="w-full space-y-6">
      <div className="flex flex-col container mx-auto max-w-3xl">
        <Card className="text-center">
          <CardHeader>
            <CardTitle className="text-xl font-semibold tracking-tight">
              Payment Cancelled
            </CardTitle>
          </CardHeader>

          <CardContent className="text-center space-y-6">
            <Alert variant="destructive" className="text-left">
              <AlertCircle className="h-6 w-6 text-red-600" />
              <AlertTitle>Important</AlertTitle>
              <AlertDescription>
                Your registration is not valid and will not be reviewed until
                payment is completed.
              </AlertDescription>
            </Alert>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              {sessionId && isLicenseLoading ? (
                <Button
                  variant="outline"
                  size="lg"
                  className="h-12 px-8"
                  disabled
                >
                  <Home className="h-5 w-5 mr-2" />
                  Go to Dashboard
                </Button>
              ) : licenseData?.license_id ? (
                <Link
                  to="/registrant/dashboard"
                  search={{
                    openLicense: licenseData.license_id,
                    focusProperty: true,
                  }}
                >
                  <Button variant="outline" size="lg" className="h-12 px-8">
                    <Home className="h-5 w-5 mr-2" />
                    Go to Dashboard
                  </Button>
                </Link>
              ) : (
                <Link to="/registrant/dashboard">
                  <Button variant="outline" size="lg" className="h-12 px-8">
                    <Home className="h-5 w-5 mr-2" />
                    Go to Dashboard
                  </Button>
                </Link>
              )}
              {canMakePayments &&
              sessionId &&
              (isBulkPaymentFlow || licenseData?.license_id) ? (
                <Button
                  size="lg"
                  className="h-12 px-8"
                  onClick={handlePayNow}
                  disabled={
                    !isAuthSetupComplete ||
                    (!isBulkPaymentFlow &&
                      (isLicenseLoading || !licenseData?.license_id))
                  }
                >
                  <CreditCard className="h-5 w-5 mr-2" />
                  Pay Now
                </Button>
              ) : null}
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
